/**
 * OVERRIDE Edit.jsx
 * REASON: No need of title and description in the tabs block as per the requirement.
 * FILE: https://github.com/eea/volto-tabs-block/blob/9da0b6998d89ecc10ff5e1bf4dd6b261a9a11ff0/src/components/templates/default/Edit.jsx
 * FILE VERSION: eea/volto-tabs-block 8.1.0
 * PULL REQUEST: https://gitlab.fhnw.ch/webteam/fhnw-webauftritt-2025/-/merge_requests/405
 * TICKET: https://gitlab.fhnw.ch/webteam/fhnw-webauftritt-2025/-/issues/390
 * DATE: 2024-12-09
 * DEVELOPER: @tishasoumya
 * CHANGELOG:
 *  - Remove the title and description fields as not required as per the requirements @tishasoumya
 *
 * Depending on the nature of the change, if possible or suitable, mark the change with a JSX comment at the beginning and end of the change:
 *
 *   START CUSTOMIZATION
 *   <CUSTOMIZATION>
 *   END CUSTOMIZATION
 */

import React from 'react';
import { useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import { v4 as uuid } from 'uuid';
import cx from 'classnames';
import { Menu, Tab, Input, Container } from 'semantic-ui-react';
import { BlocksForm } from '@plone/volto/components';
import { emptyBlocksForm } from '@plone/volto/helpers';

import EditBlockWrapper from './../../EditBlockWrapper.jsx';
import { AssetTab } from './TabComponents';
import { getMenuPosition } from '@eeacms/volto-tabs-block/utils';

import noop from 'lodash/noop';

export const MenuItem = (props) => {
  const inputRef = React.useRef(null);
  const intl = useIntl();
  const {
    schema,
    activeTab = null,
    activeBlock = null,
    block = null,
    data = {},
    editingTab = null,
    selected = false,
    tabData = {},
    tabs = {},
    // START CUSTOMIZATION
    // tabsDescription,
    // tabsTitle,
    // END CUSTOMIZATION
    tabsData = {},
    tabsList = [],
    emptyTab = () => {},
    setActiveBlock = noop,
    setActiveTab = noop,
    setEditingTab = noop,
    onChangeBlock = noop,
  } = props;
  const { tab, index } = props;
  const tabIndex = index + 1;
  const defaultTitle = `Tab ${tabIndex}`;
  const tabSettings = tabs[tab];
  const { title, assetType } = tabSettings;
  const tabTitle = title || defaultTitle;

  const addNewTab = () => {
    const tabId = uuid();

    onChangeBlock(block, {
      ...data,
      data: {
        ...tabsData,
        blocks: {
          ...tabsData.blocks,
          [tabId]: {
            ...emptyTab({
              schema: schema?.properties?.data?.schema || {},
              intl,
            }),
          },
        },
        blocks_layout: {
          items: [...tabsData.blocks_layout.items, tabId],
        },
      },
    });
    return tabId;
  };

  React.useEffect(() => {
    if (editingTab === tab && inputRef.current) {
      inputRef.current.focus();
    }
    /* eslint-disable-next-line */
  }, [editingTab]);

  return (
    <>
      {/* START CUSTOMIZATION */}
      {/* {index === 0 && (tabsTitle || tabsDescription) && (
        <Menu.Item className="menu-title">
          <SimpleMarkdown md={tabsTitle} className="title" defaultTag="##" />
          <SimpleMarkdown md={tabsDescription} className="description" />
        </Menu.Item>
      )} */}
      {/* END CUSTOMIZATION */}
      <Menu.Item
        name={defaultTitle}
        active={tab === activeTab}
        className="remove-margin"
        tabIndex={0}
        role={'tab'}
        onKeyDown={(e) => {
          if (e.target.tagName === 'A' && e.code === 'Space') {
            e.preventDefault();
            setActiveTab(tab);
          }
        }}
        onClick={() => {
          if (activeTab !== tab) {
            setActiveTab(tab);
          }
          if (activeBlock) {
            setActiveBlock(null);
          }
          if (editingTab !== tab) {
            setEditingTab(null);
          }
        }}
        onDoubleClick={() => {
          setEditingTab(tab);
        }}
      >
        {editingTab === tab && selected ? (
          <Input
            placeholder={defaultTitle}
            ref={inputRef}
            transparent
            value={title}
            onChange={(e) => {
              onChangeBlock(block, {
                ...data,
                data: {
                  ...tabsData,
                  blocks: {
                    ...tabsData.blocks,
                    [tab]: {
                      ...(tabData || {}),
                      title: e.target.value,
                    },
                  },
                },
              });
            }}
          />
        ) : (
          <>
            {assetType ? (
              <AssetTab
                props={tabSettings}
                tabTitle={tabTitle}
                tabIndex={tabIndex}
              />
            ) : (
              <span>{tabTitle}</span>
            )}
          </>
        )}
      </Menu.Item>
      {index === tabsList.length - 1 ? (
        <>
          <Menu.Item
            tabIndex={0}
            role="tab"
            name="addition"
            onKeyDown={(e) => {
              if (e.code === 'Space') {
                e.preventDefault();
                const newTab = addNewTab();
                setActiveTab(newTab);
              }
            }}
            onClick={() => {
              const newTab = addNewTab();
              setActiveTab(newTab);
              setEditingTab(null);
            }}
            className="remove-margin addition-button"
          >
            <span className="menu-item-text">+</span>
          </Menu.Item>
        </>
      ) : (
        ''
      )}
    </>
  );
};

const Edit = (props) => {
  const intl = useIntl();
  const {
    activeBlock = null,
    activeTab = null,
    activeTabIndex = 0,
    block = null,
    data = {},
    selected = false,
    editingTab = null,
    manage = false,
    metadata = null,
    multiSelected = [],
    tabs = {},
    tabsData = {},
    tabsList = [],
    emptyTab = () => {},
    onChangeBlock = noop,
    onChangeTabData = noop,
    onSelectBlock = noop,
    setEditingTab = noop,
    schema,
    skipColorOption = false,
    //START CUSTOMIZATION
    // customTabsClass = '',
    //END CUSTOMIZATION
  } = props;
  const menuPosition = getMenuPosition(data);
  const {
    //START CUSTOMIZATION
    // title,
    // description,
    // align,
    //END CUSTOMIZATION
    menuBorderless,
    menuColor,
    menuCompact,
    menuFluid,
    menuInverted,
    menuPointing,
    menuSecondary,
    menuSize,
    menuStackable,
    menuTabular,
    menuText,
    menuAlign,
  } = data;
  //START CUSTOMIZATION
  const isContainer = false;
  //END CUSTOMIZATION
  const panes = tabsList.map((tab, index) => {
    return {
      id: tab,
      menuItem: (
        <MenuItem
          {...props}
          key={tab}
          editingTab={editingTab}
          index={index}
          setEditingTab={setEditingTab}
          tab={tab}
          //START CUSTOMIZATION
          // tabsTitle={title}
          // tabsDescription={description}
          //END CUSTOMIZATION
          schema={schema}
        />
      ),
      render: () => {
        return (
          <Tab.Pane as={isContainer ? Container : undefined}>
            <BlocksForm
              errors={props.errors}
              allowedBlocks={data?.allowedBlocks}
              description={data?.instructions?.data}
              manage={manage}
              isMainForm={false}
              metadata={metadata}
              pathname={props.pathname}
              properties={isEmpty(tabs[tab]) ? emptyBlocksForm() : tabs[tab]}
              selected={selected && activeTab === tab && activeBlock}
              selectedBlock={
                selected && activeTab === tab && activeBlock
                  ? activeBlock
                  : null
              }
              title={data?.placeholder}
              onChangeField={onChangeTabData}
              onChangeFormData={(newFormData) => {
                onChangeBlock(block, {
                  ...data,
                  data: {
                    ...tabsData,
                    blocks: {
                      ...tabsData.blocks,
                      [activeTab]: {
                        ...(newFormData.blocks_layout.items.length > 0
                          ? newFormData
                          : emptyTab({
                              schema: schema?.properties?.data?.schema || {},
                              intl,
                            })),
                      },
                    },
                  },
                });
              }}
              onSelectBlock={(id, selected, e) => {
                const isMultipleSelection = e
                  ? e.shiftKey || e.ctrlKey || e.metaKey
                  : false;
                onSelectBlock(
                  id,
                  activeBlock === id ? false : isMultipleSelection,
                  e,
                );
                setEditingTab(null);
              }}
            >
              {({ draginfo }, editBlock, blockProps) => {
                return (
                  <EditBlockWrapper
                    blockProps={blockProps}
                    draginfo={draginfo}
                    multiSelected={multiSelected.includes(blockProps.block)}
                  >
                    {editBlock}
                  </EditBlockWrapper>
                );
              }}
            </BlocksForm>
          </Tab.Pane>
        );
      },
    };
  });
  return (
    <>
      <Tab
        activeIndex={activeTabIndex}
        //START CUSTOMIZATION
        //className={cx('default tabs', customTabsClass)}
        //END CUSTOMIZATION
        menu={{
          attached: menuPosition.attached,
          borderless: menuBorderless,
          color: !skipColorOption && menuColor,
          compact: menuCompact,
          fluid: menuFluid,
          inverted: menuInverted,
          pointing: menuPointing,
          secondary: menuSecondary,
          size: menuSize,
          stackable: menuStackable,
          tabular: menuTabular,
          text: menuText,
          vertical: menuPosition.vertical,
          className: cx(
            'tabs-secondary-variant',
            data.menuAlign,
            menuAlign,
            menuPosition.direction === 'left' ? 'border-right' : '',
            menuPosition.direction === 'right' ? 'border-left' : '',
            menuPosition.direction === 'top' ? 'border-bottom' : '',
            menuPosition.direction === 'bottom' ? 'border-top' : '',
            { container: isContainer },
            props.addTabsOptions ? props.addTabsOptions(data) : '',
          ),
        }}
        menuPosition={menuPosition.direction}
        panes={panes}
        grid={{ paneWidth: 9, tabWidth: 3 }}
      />
    </>
  );
};
//START CUSTOMIZATION
// Edit.schemaEnhancer = defaultSchemaEnhancer;
//END CUSTOMIZATION
export default Edit;
